import React, { useCallback, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import {
  Breadcrumb,
  BreadcrumbItem,
  Button,
  Card,
  CardBody,
  CardTitle,
  Content,
  DescriptionList,
  DescriptionListDescription,
  DescriptionListGroup,
  DescriptionListTerm,
  EmptyState,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateFooter,
  Flex,
  FlexItem,
  Icon,
  Label,
  PageBreadcrumb,
  PageSection,
  PageSectionVariants,
  Pagination,
  Tab,
  TabTitleText,
  Tabs,
  Tooltip,
} from '@patternfly/react-core';
import { Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import {
  BalanceScaleIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  SyncAltIcon,
  UndoIcon,
  WalletIcon,
} from '@patternfly/react-icons';
import CenteredSpinner from '@app/CenteredSpinner';
import Unauthenticated from '@app/Unauthenticated';
import { NotFound } from '@app/NotFound/NotFound';
import { useApiFetch } from '../../../api';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useUserContext } from '../../../contexts/user/UserContext';
import { useAdminWallet } from '../../../hooks/useAdminWallets';
import { formatDateTime } from '../../../utils/dateFormat';
import { formatPrice } from '../../../utils/money';
import type {
  AdminWalletDeposit,
  AdminWalletInvoicePayment,
  AdminWalletInvoicePaymentStatus,
  AdminWalletResolveResult,
  AdminWalletTransaction,
  WalletDepositStatus,
} from '../../../types/models/Wallet';
import type { ApiResponseAdminWalletResolve } from '../../../types/api/ApiResponseAdminWallet';
import {
  BalanceAmount,
  SignedAmount,
  TransactionKindLabel,
  isNegativeMoney,
} from '../../Organization/Billing/paymentDisplay';
import WalletAdjustModal from './WalletAdjustModal';
import WalletRefundModal from './WalletRefundModal';
import WalletCancelRefundModal from './WalletCancelRefundModal';
import UnpaidInvoicesTab from './UnpaidInvoicesTab';
import {
  isCancellableDeposit,
  isRefundableDeposit,
  isResolvableInvoicePayment,
  refundableRemainder,
} from './walletUtils';

type DetailTabKey = 'transactions' | 'deposits' | 'invoice-payments' | 'unpaid-invoices';

function DepositStatusLabel({ deposit }: { deposit: AdminWalletDeposit }) {
  const s: WalletDepositStatus = deposit.status;
  if (deposit.credited || s === 'paid') {
    return (
      <Label isCompact color="green">
        {deposit.credited ? 'Credited' : 'Paid'}
      </Label>
    );
  }
  if (s === 'failed') {
    return (
      <Label isCompact color="red">
        Failed
      </Label>
    );
  }
  if (s === 'expired' || s === 'canceled') {
    return (
      <Label isCompact color="orange">
        {s === 'expired' ? 'Expired' : 'Canceled'}
      </Label>
    );
  }
  return (
    <Label isCompact color="blue">
      In progress ({s})
    </Label>
  );
}

/** Per-outcome copy for the Resolve notification (docs/api-types/admin-wallets.md — Resolve). */
const RESOLVE_OUTCOME_COPY: Record<AdminWalletResolveResult['outcome'], (numero: string) => string> = {
  paid: (n) => `Invoice #${n} is confirmed paid; the charge stands.`,
  reversed: (n) => `Facturier shows invoice #${n} unpaid; the charge was reversed and the customer may pay again.`,
  released: (n) =>
    `No payment exists at Mollie for invoice #${n}; the saved-card claim was released and the customer may pay again.`,
  pending: (n) =>
    `The saved-card claim for invoice #${n} is still too young to release (under 10 minutes) and no payment was found at Mollie yet — check again later.`,
};

const invoicePaymentSourceText = (p: AdminWalletInvoicePayment): string =>
  p.source === 'card' ? (p.mollie_payment_id ? 'Saved card' : 'Saved card (unlinked)') : 'Wallet';

function InvoicePaymentStatusLabel({ status }: { status: AdminWalletInvoicePaymentStatus }) {
  switch (status) {
    case 'paid':
      return (
        <Label isCompact color="green">
          Paid
        </Label>
      );
    case 'reversed':
      return (
        <Label isCompact color="grey">
          Reversed
        </Label>
      );
    case 'needs_reconciliation':
      return (
        <Label isCompact color="orange" icon={<ExclamationTriangleIcon />}>
          Needs reconciliation
        </Label>
      );
    case 'settling':
    default:
      return (
        <Label isCompact color="blue">
          Settling
        </Label>
      );
  }
}

/** Client-side paginated table of the last 200 ledger rows (newest first). */
const TransactionsTable: React.FunctionComponent<{ rows: AdminWalletTransaction[] }> = ({ rows }) => {
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const paginated = useMemo(() => rows.slice((page - 1) * perPage, page * perPage), [rows, page, perPage]);
  if (rows.length === 0) {
    return <Content component="p">No movement on this wallet yet.</Content>;
  }
  const pagination = (variant?: 'bottom') => (
    <Pagination
      itemCount={rows.length}
      perPage={perPage}
      page={page}
      onSetPage={(_e, p) => setPage(p)}
      onPerPageSelect={(_e, pp) => {
        setPerPage(pp);
        setPage(1);
      }}
      variant={variant}
    />
  );
  return (
    <>
      {pagination()}
      <Table aria-label="Wallet transactions" variant="compact">
        <Thead>
          <Tr>
            <Th>Date</Th>
            <Th>Kind</Th>
            <Th>Description</Th>
            <Th>Reference</Th>
            <Th className="pf-v6-u-text-align-end">Amount</Th>
            <Th className="pf-v6-u-text-align-end">Balance after</Th>
            <Th>By</Th>
          </Tr>
        </Thead>
        <Tbody>
          {paginated.map((t) => (
            <Tr key={t.id}>
              <Td dataLabel="Date">{formatDateTime(t.created_at)}</Td>
              <Td dataLabel="Kind">
                <TransactionKindLabel kind={t.kind} amount={t.amount} />
              </Td>
              <Td dataLabel="Description">{t.description}</Td>
              <Td dataLabel="Reference">{t.reference_label ?? '—'}</Td>
              <Td dataLabel="Amount" className="pf-v6-u-text-align-end">
                <SignedAmount value={t.amount} />
              </Td>
              <Td dataLabel="Balance after" className="pf-v6-u-text-align-end">
                {formatPrice(t.balance_after)}
              </Td>
              <Td dataLabel="By">{t.user_id ?? '—'}</Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
      {pagination('bottom')}
    </>
  );
};

/** Credited, paid through Mollie, and fully refunded already: nothing left for the Refund action. */
const isFullyRefunded = (d: AdminWalletDeposit) =>
  d.credited && d.mollie_payment_id !== null && !isRefundableDeposit(d) && refundableRemainder(d) === '0.00';

const DepositsTable: React.FunctionComponent<{
  rows: AdminWalletDeposit[];
  onRefund: (deposit: AdminWalletDeposit) => void;
  onCancelRefund: (deposit: AdminWalletDeposit) => void;
}> = ({ rows, onRefund, onCancelRefund }) => {
  if (rows.length === 0) {
    return <Content component="p">No top-up started yet.</Content>;
  }
  return (
    <Table aria-label="Wallet top-ups" variant="compact">
      <Thead>
        <Tr>
          <Th>Started</Th>
          <Th className="pf-v6-u-text-align-end">Amount</Th>
          <Th>Source</Th>
          <Th>Status</Th>
          <Th>Credited at</Th>
          <Th>Mollie payment</Th>
          <Th className="pf-v6-u-text-align-end">Clawback</Th>
          <Th screenReaderText="Actions" />
        </Tr>
      </Thead>
      <Tbody>
        {rows.map((d) => (
          <Tr key={d.id}>
            <Td dataLabel="Started">{formatDateTime(d.created_at)}</Td>
            <Td dataLabel="Amount" className="pf-v6-u-text-align-end">
              {formatPrice(d.amount)}
            </Td>
            <Td dataLabel="Source">{d.source === 'card' ? 'Saved card' : 'Checkout'}</Td>
            <Td dataLabel="Status">
              <DepositStatusLabel deposit={d} />
            </Td>
            <Td dataLabel="Credited at">{formatDateTime(d.credited_at)}</Td>
            <Td dataLabel="Mollie payment">{d.mollie_payment_id ? <code>{d.mollie_payment_id}</code> : '—'}</Td>
            <Td dataLabel="Clawback" className="pf-v6-u-text-align-end">
              {d.clawback_amount ? formatPrice(d.clawback_amount) : '—'}
            </Td>
            <Td dataLabel="Actions" className="pf-v6-u-text-align-end">
              <Flex
                spaceItems={{ default: 'spaceItemsSm' }}
                justifyContent={{ default: 'justifyContentFlexEnd' }}
                flexWrap={{ default: 'nowrap' }}
              >
                {isRefundableDeposit(d) ? (
                  <FlexItem>
                    <Button
                      variant="secondary"
                      size="sm"
                      icon={<UndoIcon />}
                      onClick={() => onRefund(d)}
                      aria-label={`Refund top-up #${d.id}`}
                    >
                      Refund
                    </Button>
                  </FlexItem>
                ) : isFullyRefunded(d) ? (
                  <FlexItem className="pf-v6-u-font-size-sm pf-v6-u-text-color-subtle">Fully refunded</FlexItem>
                ) : null}
                {isCancellableDeposit(d) && (
                  <FlexItem>
                    <Button
                      variant="link"
                      size="sm"
                      onClick={() => onCancelRefund(d)}
                      aria-label={`Cancel the recorded refund of top-up #${d.id}`}
                    >
                      Cancel refund
                    </Button>
                  </FlexItem>
                )}
              </Flex>
            </Td>
          </Tr>
        ))}
      </Tbody>
    </Table>
  );
};

/**
 * Admin › Wallets › {client} — one customer's prepaid wallet (GET /admin/wallets/{clientId}): the
 * balance, the last 200 ledger rows, the last 50 top-ups (with Mollie ids and clawbacks) and the
 * wallet settlement attempts on Facturier invoices, where a parked `needs_reconciliation` row (or a
 * `settling` attempt older than 10 minutes) can be resolved. The "Unpaid invoices" tab charges the
 * organization's unpaid invoices on its own automatic-payment consent (`UnpaidInvoicesTab`). Staff-only
 * (self-guard mirrors the routes' `adminOnly` flag).
 */
export function WalletDetail() {
  const auth = useAuth();
  const user = useUserContext();
  const isStaff = user?.is_ancalagon_staff === true;
  const { clientId: clientIdParam } = useParams<{ clientId: string }>();
  const apiFetch = useApiFetch();
  const { addNotification, setDrawerOpen } = useNotifications();

  const clientId = useMemo<number | null>(() => {
    const parsed = Number(clientIdParam);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
  }, [clientIdParam]);

  const { detail, loading, error, notFound, refetch } = useAdminWallet(isStaff ? clientId : null);
  const [activeTab, setActiveTab] = useState<DetailTabKey>('transactions');
  const [adjustOpen, setAdjustOpen] = useState(false);
  // The top-up as displayed when Refund was clicked (the refund form echoes its clawback_amount).
  const [refundTarget, setRefundTarget] = useState<AdminWalletDeposit | null>(null);
  const [cancelTarget, setCancelTarget] = useState<AdminWalletDeposit | null>(null);
  const [resolvingId, setResolvingId] = useState<number | null>(null);

  const resolve = useCallback(
    async (payment: AdminWalletInvoicePayment) => {
      if (clientId === null) {
        return;
      }
      setResolvingId(payment.id);
      try {
        const response = await apiFetch(`/admin/wallets/${clientId}/invoice-payments/${payment.id}/resolve`, {
          method: 'POST',
        });
        // JSend: branch on body.status, never response.ok.
        const body = (await response.json().catch(() => null)) as ApiResponseAdminWalletResolve | null;
        if (body?.status === 'success' && body.data?.outcome) {
          addNotification({
            title: `Resolved: ${body.data.outcome}`,
            srTitle: 'Success alert',
            description: RESOLVE_OUTCOME_COPY[body.data.outcome](payment.invoice_numero),
            variant: body.data.outcome === 'pending' ? 'info' : 'success',
          });
          setDrawerOpen(true);
          void refetch();
          return;
        }
        addNotification({
          title: 'Could not resolve this payment',
          srTitle: 'Error alert',
          description: body?.message ?? 'An unexpected error occurred.',
          variant: 'danger',
        });
        setDrawerOpen(true);
      } catch (err) {
        addNotification({
          title: 'Could not resolve this payment',
          srTitle: 'Error alert',
          description: err instanceof Error ? err.message : 'An unexpected error occurred.',
          variant: 'danger',
        });
        setDrawerOpen(true);
      } finally {
        setResolvingId(null);
      }
    },
    [apiFetch, clientId, addNotification, setDrawerOpen, refetch],
  );

  if (!auth.isAuthenticated) {
    return <Unauthenticated />;
  }
  if (!isStaff) {
    return <NotFound />;
  }

  const clientName = detail?.client.name ?? (clientId !== null ? `Client #${clientId}` : 'Wallet');
  const showActions = !loading && !error && !notFound && detail !== null;

  const renderInvoicePayments = (rows: AdminWalletInvoicePayment[]) => {
    if (rows.length === 0) {
      return <Content component="p">No invoice has been paid from this wallet yet.</Content>;
    }
    return (
      <Table aria-label="Wallet invoice payments" variant="compact">
        <Thead>
          <Tr>
            <Th>Created</Th>
            <Th>Invoice #</Th>
            <Th>Source</Th>
            <Th className="pf-v6-u-text-align-end">Amount</Th>
            <Th>Status</Th>
            <Th>Facturier marked paid</Th>
            <Th screenReaderText="Actions" />
          </Tr>
        </Thead>
        <Tbody>
          {rows.map((p) => (
            <Tr key={p.id}>
              <Td dataLabel="Created">{formatDateTime(p.created_at)}</Td>
              <Td dataLabel="Invoice #">{p.invoice_numero}</Td>
              <Td dataLabel="Source">{invoicePaymentSourceText(p)}</Td>
              <Td dataLabel="Amount" className="pf-v6-u-text-align-end">
                {formatPrice(p.amount)}
              </Td>
              <Td dataLabel="Status">
                <InvoicePaymentStatusLabel status={p.status} />
              </Td>
              <Td dataLabel="Facturier marked paid">{formatDateTime(p.facturier_marked_paid_at)}</Td>
              <Td dataLabel="Actions" isActionCell>
                {isResolvableInvoicePayment(p) && (
                  <Tooltip
                    content={
                      p.source === 'card' && !p.mollie_payment_id
                        ? "Unlinked saved-card claim (the create response was lost). Resolve searches the customer's Mollie payments: found ⇒ linked and settled; absent after 10 minutes ⇒ released so the customer may pay again."
                        : p.status === 'settling'
                          ? 'This settlement attempt has been in flight for more than 10 minutes — the request most likely died mid-way. Resolve re-reads the invoice at Facturier.'
                          : 'Re-read the invoice at Facturier: paid ⇒ the charge stands; unpaid ⇒ the charge is reversed and the customer may pay again.'
                    }
                  >
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => void resolve(p)}
                      isLoading={resolvingId === p.id}
                      isDisabled={resolvingId !== null}
                    >
                      Resolve
                    </Button>
                  </Tooltip>
                )}
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    );
  };

  const renderContent = () => {
    if (loading && detail === null) {
      return <CenteredSpinner />;
    }
    if (notFound) {
      return (
        <EmptyState titleText="Organization not found" icon={WalletIcon} headingLevel="h2" variant="lg">
          <EmptyStateBody>No organization matches this id.</EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="primary" component={(props) => <Link {...props} to="/admin/wallets" />}>
                Back to wallets
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      );
    }
    if (error || detail === null) {
      return (
        <EmptyState
          titleText="Unable to load this wallet"
          icon={ExclamationCircleIcon}
          status="danger"
          headingLevel="h2"
          variant="lg"
        >
          <EmptyStateBody>{error ?? 'Unknown error'}</EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="primary" onClick={() => void refetch()}>
                Retry
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      );
    }
    const negative = isNegativeMoney(detail.wallet.balance);
    return (
      <>
        <Card className="pf-v6-u-mb-md">
          <CardTitle>Balance</CardTitle>
          <CardBody>
            <DescriptionList isHorizontal isCompact>
              <DescriptionListGroup>
                <DescriptionListTerm>Balance</DescriptionListTerm>
                <DescriptionListDescription>
                  <BalanceAmount value={detail.wallet.balance} />
                  {negative && (
                    <Label color="orange" icon={<ExclamationTriangleIcon />} className="pf-v6-u-ml-sm">
                      Negative — receivable
                    </Label>
                  )}
                </DescriptionListDescription>
              </DescriptionListGroup>
              <DescriptionListGroup>
                <DescriptionListTerm>Currency</DescriptionListTerm>
                <DescriptionListDescription>{detail.wallet.currency}</DescriptionListDescription>
              </DescriptionListGroup>
            </DescriptionList>
          </CardBody>
        </Card>
        <Tabs
          activeKey={activeTab}
          onSelect={(_e, key) => setActiveTab(key as DetailTabKey)}
          aria-label="Wallet detail sections"
          mountOnEnter
        >
          <Tab eventKey="transactions" title={<TabTitleText>Transactions ({detail.transactions.length})</TabTitleText>}>
            <div className="pf-v6-u-pt-md">
              <TransactionsTable rows={detail.transactions} />
            </div>
          </Tab>
          <Tab eventKey="deposits" title={<TabTitleText>Top-ups ({detail.deposits.length})</TabTitleText>}>
            <div className="pf-v6-u-pt-md">
              <DepositsTable rows={detail.deposits} onRefund={setRefundTarget} onCancelRefund={setCancelTarget} />
            </div>
          </Tab>
          <Tab
            eventKey="invoice-payments"
            title={<TabTitleText>Invoice payments ({detail.invoice_payments.length})</TabTitleText>}
          >
            <div className="pf-v6-u-pt-md">{renderInvoicePayments(detail.invoice_payments)}</div>
          </Tab>
          <Tab eventKey="unpaid-invoices" title={<TabTitleText>Unpaid invoices</TabTitleText>}>
            <div className="pf-v6-u-pt-md">
              {clientId !== null && (
                <UnpaidInvoicesTab
                  clientId={clientId}
                  isActive={activeTab === 'unpaid-invoices'}
                  onCharged={() => void refetch()}
                />
              )}
            </div>
          </Tab>
        </Tabs>
      </>
    );
  };

  return (
    <>
      <PageBreadcrumb hasBodyWrapper={false} hasShadowBottom={false}>
        <Breadcrumb ouiaId="BasicBreadcrumb">
          <BreadcrumbItem>Admin</BreadcrumbItem>
          <BreadcrumbItem
            render={({ className }) => (
              <Link className={className} to="/admin/wallets">
                Wallets
              </Link>
            )}
          />
          <BreadcrumbItem isActive>{clientName}</BreadcrumbItem>
        </Breadcrumb>
      </PageBreadcrumb>
      <PageSection hasBodyWrapper variant={PageSectionVariants.default} isWidthLimited hasShadowBottom={true}>
        <Flex
          justifyContent={{ default: 'justifyContentSpaceBetween' }}
          alignItems={{ default: 'alignItemsFlexStart' }}
        >
          <FlexItem>
            <Content component="h1">
              <Icon size="lg" style={{ marginRight: '8px' }}>
                <WalletIcon />
              </Icon>
              {clientName}
            </Content>
            <Content component="p">Prepaid wallet of this organization</Content>
          </FlexItem>
          {showActions && (
            <FlexItem>
              <Flex spaceItems={{ default: 'spaceItemsSm' }}>
                <FlexItem>
                  <Button
                    variant="secondary"
                    icon={<SyncAltIcon />}
                    onClick={() => void refetch()}
                    isLoading={loading}
                    isDisabled={loading}
                  >
                    Refresh
                  </Button>
                </FlexItem>
                <FlexItem>
                  <Button variant="primary" icon={<BalanceScaleIcon />} onClick={() => setAdjustOpen(true)}>
                    Adjust
                  </Button>
                </FlexItem>
              </Flex>
            </FlexItem>
          )}
        </Flex>
      </PageSection>
      <PageSection hasBodyWrapper={false}>{renderContent()}</PageSection>
      {detail && clientId !== null && (
        <WalletAdjustModal
          isOpen={adjustOpen}
          onClose={() => setAdjustOpen(false)}
          onSaved={() => void refetch()}
          clientId={clientId}
          clientName={detail.client.name}
          currentBalance={detail.wallet.balance}
        />
      )}
      {detail && clientId !== null && refundTarget !== null && (
        <WalletRefundModal
          deposit={refundTarget}
          clientId={clientId}
          clientName={detail.client.name}
          onClose={() => setRefundTarget(null)}
          onReload={() => void refetch()}
        />
      )}
      {detail && clientId !== null && cancelTarget !== null && (
        <WalletCancelRefundModal
          deposit={cancelTarget}
          clientId={clientId}
          clientName={detail.client.name}
          onClose={() => setCancelTarget(null)}
          onReload={() => void refetch()}
        />
      )}
    </>
  );
}

export default WalletDetail;
